import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Alert } from 'components/ui/alert'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import { login, registerNewUser } from 'slices/authSlice'

const validationSchema = Yup.object().shape({
	name: Yup.string().required('Full name is required'),
	firstName: Yup.string().required('First name is required'),
	email: Yup.string().email('Invalid email').required('Email is required'),
	password: Yup.string()
		.min(8, 'Password must be at least 8 characters')
		.matches(/[A-Z]/, 'Must contain at least one uppercase letter')
		.matches(/[0-9]/, 'Must contain at least one number')
		.matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, 'Must contain at least one special character')
		.required('Password is required'),
	passwordConfirm: Yup.string()
		.oneOf([Yup.ref('password')], 'Passwords must match')
		.required('Please confirm your password'),
})

const EmailConfirmFormCN = () => {
	const [errorMessage, setErrorMessage] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const dispatch = useDispatch()

	const userLogin = useSelector((state: any) => state.userLogin)
	const { loading: userLoginLoading } = userLogin

	const urlParams = new URLSearchParams(window.location.search)

	const initialName = urlParams.get('name') || ''
	const initialValues = {
		name: initialName,
		firstName: urlParams.get('firstname') || initialName.split(' ')[0] || '',
		email: urlParams.get('email') || '',
		password: '',
		passwordConfirm: '',
	}

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={async (values, { setSubmitting }) => {
				setErrorMessage('')
				const { name, firstName, email, password } = values

				try {
					await dispatch(registerNewUser({ name, firstName, email, password }))
					await dispatch(login({ email, password }))
				} catch (err: any) {
					setErrorMessage(err?.message || 'Account creation failed. Please try again.')
				} finally {
					setSubmitting(false)
				}
			}}
		>
			{({ isSubmitting, handleChange, handleBlur, values, errors, touched, setFieldValue }) => (
				<Form className="space-y-5">
					<h2 className="text-2xl font-semibold text-center text-[#074f9b]">
						Create Your Account
					</h2>

					{errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor="ec-name">Full Name</Label>
							<Input
								id="ec-name"
								name="name"
								value={values.name}
								onChange={e => {
									handleChange(e)
									if (e.target.value) {
										setFieldValue('firstName', e.target.value.split(' ')[0])
									}
								}}
								onBlur={handleBlur}
								placeholder="Full Name"
								className="h-11"
							/>
							{touched.name && errors.name && (
								<p className="text-xs text-red-500 mt-1">{errors.name}</p>
							)}
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="ec-firstName">First Name</Label>
							<Input
								id="ec-firstName"
								name="firstName"
								value={values.firstName}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder="First Name"
								className="h-11"
							/>
							{touched.firstName && errors.firstName && (
								<p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
							)}
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="ec-email">Email</Label>
							<Input
								id="ec-email"
								name="email"
								type="email"
								value={values.email}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder="Email"
								className="h-11"
							/>
							{touched.email && errors.email && (
								<p className="text-xs text-red-500 mt-1">{errors.email}</p>
							)}
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="ec-password">Password</Label>
							<div className="relative">
								<Input
									id="ec-password"
									name="password"
									type={showPassword ? 'text' : 'password'}
									value={values.password}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Minimum 8 characters"
									className="h-11 pr-10"
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
								>
									{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
								</button>
							</div>
							{touched.password && errors.password && (
								<p className="text-xs text-red-500 mt-1">{errors.password}</p>
							)}
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="ec-passwordConfirm">Confirm Password</Label>
							<Input
								id="ec-passwordConfirm"
								name="passwordConfirm"
								type={showPassword ? 'text' : 'password'}
								value={values.passwordConfirm}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder="Re-enter your password"
								className="h-11"
							/>
							{touched.passwordConfirm && errors.passwordConfirm && (
								<p className="text-xs text-red-500 mt-1">{errors.passwordConfirm}</p>
							)}
						</div>
					</div>

					<Button
						type="submit"
						isLarge
						disabled={isSubmitting || userLoginLoading}
						className="w-full"
					>
						{isSubmitting || userLoginLoading ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Creating Account...
							</>
						) : (
							'Create Account'
						)}
					</Button>
				</Form>
			)}
		</Formik>
	)
}

export default EmailConfirmFormCN
